import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import Header from '../Header';
import Footer from '../Footer';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from "../common/Config";

const InventorySalesReport = () => {
  const [reportrange, setReportrange] = useState('');
  const [dates, setDates] = useState([null, null]);
  const [inventoryReport, setInventoryReport] = useState([]);
  const [footerTotals, setFooterTotals] = useState({
    total_cost: '0.00',
    total_price: '0.00',
    total_box_qty: '0',
    total_piece_qty: '0',
    sale_box_qty: '0',
    sale_piece_qty: '0'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');

  const fetchReport = useCallback(async (startDate, endDate) => {
    if (!startDate || !endDate) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('draw', 1);
      formData.append('start', page * rowsPerPage);
      formData.append('length', rowsPerPage);
      formData.append('searchValue', '');
      formData.append('reportrange', reportrange);

      const response = await fetch(`${config.API_URL}/sales/report`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const json = await response.json();

      if (json && json.data && Array.isArray(json.data)) {
        setInventoryReport(json.data);
        setTotalCount(json.recordsTotal || json.data.length);
      }

      if (json) {
        let calculatedTotalCost = '0.00';
        let calculatedTotalPrice = '0.00';

        if (json.data && Array.isArray(json.data)) {
          const costSum = json.data.reduce((sum, item) => {
            const cost = parseFloat(item.cost) || 0;
            return sum + cost;
          }, 0);
          const priceSum = json.data.reduce((sum, item) => {
            const price = parseFloat(item.price) || 0;
            return sum + price;
          }, 0);

          calculatedTotalCost = costSum.toFixed(2);
          calculatedTotalPrice = priceSum.toFixed(2);
        }

        setFooterTotals({
          total_cost: json.total_cost || calculatedTotalCost,
          total_price: json.total_price || calculatedTotalPrice,
          total_box_qty: json.total_box_qty || '0',
          total_piece_qty: json.total_piece_qty || '0',
          sale_box_qty: json.sale_box_qty || '0',
          sale_piece_qty: json.sale_piece_qty || '0'
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, page, rowsPerPage, reportrange]);

  useEffect(() => {
    if (dates[0] && dates[1]) {
      fetchReport(dates[0], dates[1]);
    }
  }, [dates, fetchReport]);

  const handleDateChange = (value) => {
    setDates(value);
    if (value && value[0] && value[1]) {
      const formattedStartDate = format(value[0], 'dd/MM/yyyy');
      const formattedEndDate = format(value[1], 'dd/MM/yyyy');
      setReportrange(formattedStartDate ? formattedStartDate + ' - ' + formattedEndDate : '');
      setPage(0);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatNumber = (num) => {
    return parseFloat(num || 0).toFixed(2);
  };

  return (
    <>
      {/* <Header /> */}
      <Box className="page-container">
        <Paper
          className="paper-style"
        >
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={1} display="flex" justifyContent="space-between" alignItems="center">
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Typography className="page-title">Inventory Sales Report</Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }} justifyContent="end">
                <CommonDatePicker
                  className="input-border"
                  value={dates}
                  showOneCalendar
                  onChange={handleDateChange}
                  format="dd/MM/yyyy"
                  character=" - "
                />
              </Grid2>
            </Grid2>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="medium"
                className="table-cell-border"
                sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell
                      width="70px"
                    >
                      Code
                    </TableCell>
                    <TableCell
                      width="250px"
                    >
                      Name
                    </TableCell>
                    <TableCell
                      width="100px"
                      align="right"
                    >
                      Cost
                    </TableCell>
                    <TableCell
                      width="100px"
                      align="right"
                    >
                      Price
                    </TableCell>
                    <TableCell
                      width="100px"
                      align="right"
                    >
                      Total Box Qty
                    </TableCell>
                    <TableCell
                      width="100px"
                      align="right"
                    >
                      Total Piece Qty
                    </TableCell>
                    <TableCell
                      width="100px"
                      align="right"
                    >
                      Sale Box Qty
                    </TableCell>
                    <TableCell
                      width="100px"
                      align="right"
                    >
                      Sale Piece Qty
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventoryReport.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ color: "#637381" }}>
                          No data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    inventoryReport.map((row, index) => (
                      <TableRow
                        key={index}
                        className="table-cell-style"
                        sx={{
                          "&:last-child td": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableCell
                       
                        >
                          {row.code || ""}
                        </TableCell>
                        <TableCell
                        >
                          {row.name || ""}
                        </TableCell>
                        <TableCell
                        align="right"
                        >
                          {formatNumber(row.cost)}
                        </TableCell>
                        <TableCell
                        align="right"
                        >
                          {formatNumber(row.price)}
                        </TableCell>
                        <TableCell
                        align="right"
                        >
                          {row.total_box_qty || "0"}
                        </TableCell>
                        <TableCell
                        align="right"
                        >
                          {row.total_piece_qty || "0"}
                        </TableCell>
                        <TableCell
                        align="right"
                        >
                          {row.sale_box_qty || "0"}
                        </TableCell>
                        <TableCell
                        align="right"
                        >
                          {row.sale_piece_qty || "0"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow className="table-cell-style">
                    <TableCell colSpan={5} />
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        textAlign: "right",
                      }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                        fontWeight: 600,
                      }}
                    >
                      {footerTotals.sale_box_qty}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        textAlign: "right",
                      }}
                    >
                      {footerTotals.sale_piece_qty}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}

          {!loading && inventoryReport.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Rows per page:"
                className="pagination-styles"
              />
            </Box>
          )}
        </Paper>
      </Box>

    </>
  );
};

export default InventorySalesReport;
